// Help text for: Customizations (node settings, rated power, schedules...).
// Keys are referenced as "customization.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  // ---- Customizations page: machine cards ----
  addMachine: {
    title: "Add Machine",
    use: "Adds a machine to this screen as a card, so you can watch the readings you pick for it.",
    source: "input",
    how: "The list holds every machine your login can see that has no card yet, narrowed to the branch chosen in the header. The new card goes on the end and saves right away. If you have never saved a layout, every Grid, Generator and Solar machine is added for you — Grid first, then Generator, then Solar, each A–Z.",
  },
  saveStatus: {
    title: "Save status",
    use: "Tells you whether your card layout and reading choices are saved.",
    source: "calculated",
    how: "Compares what is on screen with the layout you last saved. Adding, removing or dragging a card saves at once. Reading choices save when you close the reading window or leave this screen.",
  },
  virtualNode: {
    title: "Virtual node",
    use: "This card is a virtual node: it has no breaker of its own to read.",
    source: "calculated",
    how: "Its power is worked out from other machines' breaker readings — using the sum set up for this node during EnViz setup, or, where none was set, by adding up the machines directly under it. Voltage, current and power factor always read zero for a virtual node.",
  },
  machinePicture: {
    title: "Machine Picture",
    use: "The photo shown on this machine's card.",
    source: "input",
    how: "Upload a JPG, PNG, WEBP or GIF up to 5 MB; it saves as soon as it finishes uploading. With no photo the card uses a stock picture for its kind — Grid, Generator, Solar or Machine — while virtual and other kinds get a plain background.",
  },
  displayParameters: {
    title: "Display Parameters",
    use: "Pick which live readings this card shows. The number in brackets is how many you have picked.",
    source: "input",
    how: "Nothing is picked to start with. Click a reading to switch it on or off. Your picks save when you close this window or leave the screen, and they only change this screen.",
  },
  paramPower: {
    title: "Power (kW)",
    use: "The power this machine is drawing, or making, right now.",
    source: "measured",
    how: "Read from the machine's breaker — the newest reading from the last 10 minutes at the moment you opened this screen, and not refreshed after that. On a 3-phase breaker the three phases are added up, changed from watts to kW, then multiplied by that node's scale factor, which is 1 unless someone changed it.",
  },
  paramVoltage: {
    title: "Voltage (V)",
    use: "The supply voltage at the machine's breaker.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen. On a 3-phase breaker it is the line-to-line voltage: the three phase voltages averaged, then multiplied by 1.732. A single-phase breaker shows the voltage of its live phase.",
  },
  paramPowerFactor: {
    title: "Power Factor",
    use: "How well the machine uses the current it pulls. 1.00 is ideal; lower means wasted current and possible penalties on your bill.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen. On a 3-phase breaker only the phases reading something other than zero are averaged.",
  },
  paramCurrent: {
    title: "Current (A)",
    use: "The current flowing through the machine's breaker.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen. On a 3-phase breaker each phase current is multiplied by that node's scale factor — 1 unless someone changed it — and the three are added up.",
  },
  paramFrequency: {
    title: "Frequency (Hz)",
    use: "The supply frequency, normally close to 50 Hz.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen, exactly as the meter reports it.",
  },
  paramThdVoltage: {
    title: "THD Voltage (%)",
    use: "How far the voltage wave is from a clean sine wave. Lower is better.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen. On a 3-phase breaker all three phases are averaged, including any reading zero.",
  },
  paramThdCurrent: {
    title: "THD Current (%)",
    use: "How far the current wave is from a clean sine wave — usually caused by drives, inverters and electronic loads. Lower is better.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen. On a 3-phase breaker all three phases are averaged, including any reading zero.",
  },
  paramTap: {
    title: "TAP (Total Active Power)",
    use: "Total active power exactly as the meter itself reports it, in kW.",
    source: "measured",
    how: "Read from the breaker at the moment you opened this screen and changed from watts to kW. Unlike Power, the node's scale factor is not applied.",
  },

  // ---- Machine tree / selectors ----
  nodeCategory: {
    title: "Category",
    use: "What kind of node this is — most often GRID (utility supply), GENERATOR, SOLAR, MACHINE (a load) or VIRTUAL (worked out from other meters).",
    source: "setting",
    how: "Chosen for each node during EnViz setup (admin Step 2); a new node starts as MACHINE. The kind decides how the node is treated elsewhere — for example only GRID meters count towards the bill forecast.",
  },
  machineHierarchy: {
    title: "Machine Hierarchy",
    use: "Your meters laid out as a tree: main supply, then branches, sections and machines.",
    source: "backend",
    how: "Built from the parent-and-child links set up during EnViz setup, which follow how your breakers actually feed one another.",
  },
  childCount: {
    title: "Items below",
    use: "How many branches, sections and machines sit directly under this one.",
    source: "calculated",
    how: "Counts only the items one step below this node in the tree, not the ones deeper down.",
  },
  includedMachines: {
    title: "Included machines",
    use: "Which machines under the chosen primary node the new user will be able to see. ✓ = included, ✕ = excluded.",
    source: "input",
    how: "Everything under the primary node is included to start with. Click a machine to leave it out; leaving out a node also hides everything under it. The user will not see a left-out machine anywhere, alerts and the bill forecast included, and picking a different primary node clears your choices.",
  },

  // ---- Report e-mail schedule (Energy Report / Energy Efficiency Report) ----
  scheduleBranch: {
    title: "Branch",
    use: "Which part of the site the e-mailed report covers.",
    source: "input",
    how: "\"Main (Whole System)\" covers every machine your login can see apart from solar and generators. Any other choice covers the machines under that branch, and only works for a branch fed from the grid. Branch names come from the nodes marked as branches during EnViz setup, with their kind in brackets; new entries come pre-filled as Main.",
  },
  recipients: {
    title: "Recipients",
    use: "The e-mail addresses that get this branch's report.",
    source: "input",
    how: "Type an address and press Enter or Add; anything that is not a valid address is turned away, and repeats are ignored. Each branch needs at least one address before the schedule can be saved, and nothing is sent to a branch with none.",
  },
  reportPeriod: {
    title: "Report period",
    use: "How often the report goes out and how much it covers. You can pick several; each one sends its own e-mail.",
    source: "input",
    how: "Comes pre-filled as Daily, which goes out once a day after the time window ends and covers the 24 hours up to that end time. Weekly goes out on Mondays covering the previous Monday to Sunday; 15 Days on the 1st and 16th covering the previous half-month; Monthly on the last day of the month covering the previous calendar month. Weekly, 15 Days and Monthly all leave between 03:00 and 04:00 Pakistan time rather than at your time window.",
  },
  timeWindows: {
    title: "Time Windows",
    use: "The daily reporting windows for this branch. Each window sends its own Daily report.",
    source: "input",
    how: "New windows come pre-filled as 09:00–17:00 (24-hour clock, Pakistan time). If the end time is earlier than the start time, the window carries over midnight. Each branch needs at least one window.",
  },
  startTime: {
    title: "Start Time",
    use: "When this reporting window starts (24-hour clock, HH:MM).",
    source: "input",
    how: "Comes pre-filled as 09:00. Together with End Time it sets the window; the Daily report goes out after the end time and covers the 24 hours up to it.",
  },
  endTime: {
    title: "End Time",
    use: "When this reporting window ends (24-hour clock, HH:MM). The Daily report goes out once this time has passed.",
    source: "input",
    how: "Comes pre-filled as 17:00. The Daily report covers the 24 hours ending at this time and goes out at most once a day per window.",
  },
  lastSent: {
    title: "Last sent",
    use: "When this window's report was last e-mailed.",
    source: "backend",
    how: "Written down after a report e-mail goes out successfully (Pakistan time). It is what stops the Daily report being sent twice on the same day.",
  },
};

export default help;
